// The chip pipeline consumes pre-escaped HTML titles — every server-rendered path
// ships attached_resource.title_html, and the client template replaceAlls the
// title straight into innerHTML. Drag payloads carry plain text (entity-decoded
// from the source card's Stimulus values), so every dispatch into that pipeline
// must escape here, exactly once.
import { escapeHtml } from '../../javascript/common/helpers/escape_html.js'

// Registered in config/initializers/mime_types.rb — used as the drag payload type so
// file-drop handlers can ignore source-card drags (and vice versa).
export const SOURCE_MIME = 'application/vnd.chat-bot-source'

export function hasSourceDrag(dataTransfer) {
  return Array.from(dataTransfer?.types || []).includes(SOURCE_MIME)
}

export function hasFileDrag(dataTransfer) {
  return Array.from(dataTransfer?.items || []).some((item) => item.kind === 'file')
}

export function setSourceDragData(dataTransfer, payload) {
  const json = JSON.stringify(payload)
  dataTransfer.setData(SOURCE_MIME, json)
  // text/plain helps some browsers show a drag label; not used for drop handling.
  dataTransfer.setData('text/plain', payload.title || `source ${payload.id}`)
  dataTransfer.effectAllowed = 'copy'
}

export function getSourceDragData(dataTransfer) {
  const raw = dataTransfer?.getData(SOURCE_MIME)
  if (!raw) return null
  try {
    const data = JSON.parse(raw)
    if (!data?.id) return null
    return data
  } catch {
    return null
  }
}

// Pending drop-to-conversation attach, scoped to a destination conversation id and a
// generation so failed/overlapping opens cannot attach to the wrong composer.
// It only lives to hand a source from the drop gesture to the composer that mounts
// moments later — if the open lands but no matching composer ever mounts (alert
// response, superseded navigation), the TTL keeps it from sitting around until
// reload, where a much later visit to the same conversation would silently attach
// a source dropped minutes ago.
const PENDING_SOURCE_ATTACH_TTL_MS = 30_000

let pendingSourceAttach = null
let pendingGeneration = 0

export function setPendingSourceAttach(payload, conversationId) {
  if (conversationId == null || conversationId === '') {
    pendingSourceAttach = null
    return null
  }

  pendingGeneration += 1
  pendingSourceAttach = {
    id: payload.id,
    // Escaped here so the composer's later re-apply (#applyDroppedSource) ships
    // the same pre-escaped HTML as an immediate attach.
    title: escapeHtml(payload.title),
    type: payload.type,
    conversationId: String(conversationId),
    generation: pendingGeneration,
    setAt: Date.now(),
  }
  return pendingGeneration
}

export function clearPendingSourceAttach(generation = null) {
  if (
    generation != null &&
    pendingSourceAttach &&
    pendingSourceAttach.generation !== generation
  ) {
    return
  }
  pendingSourceAttach = null
}

function matchesConversation(pending, conversationId) {
  if (!pending) return false
  if (conversationId == null || conversationId === '') return false
  return pending.conversationId === String(conversationId)
}

function livePending(conversationId) {
  if (!matchesConversation(pendingSourceAttach, conversationId)) return null
  if (Date.now() - pendingSourceAttach.setAt > PENDING_SOURCE_ATTACH_TTL_MS) {
    pendingSourceAttach = null
    return null
  }
  return pendingSourceAttach
}

export function peekPendingSourceAttach(conversationId) {
  return livePending(conversationId)
}

export function takePendingSourceAttach(conversationId) {
  const pending = livePending(conversationId)
  if (!pending) return null
  pendingSourceAttach = null
  return pending
}
